/**
 * Trims HTML content to a maximum character count while preserving HTML structure
 * @param html - The HTML string to trim
 * @param maxChars - Maximum number of characters (default: 400)
 * @param showEllipsis - Whether to add "..." when content is trimmed (default: true)
 * @returns Trimmed HTML string with preserved tags
 */
export const trimHtmlContent = (
    html: string,
    maxChars: number = 400,
    showEllipsis: boolean = true
): string => {
    if (!html) {
        return html
    }

    // Create a temporary DOM element to safely parse HTML
    const temp = document.createElement('div')
    temp.innerHTML = html

    let charCount = 0
    let result = ''
    let trimmed = false

    const traverse = (node: Node): boolean => {
        if (trimmed || charCount >= maxChars) {
            return false
        }

        if (node.nodeType === Node.TEXT_NODE) {
            const text = node.textContent || ''
            const remaining = maxChars - charCount

            if (text.length <= remaining) {
                result += text
                charCount += text.length
            } else {
                result += text.substring(0, remaining)
                charCount = maxChars
                trimmed = true
                return false
            }
        } else if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node as Element
            const tagName = element.tagName.toLowerCase()

            // Collect all attributes
            let attributesStr = ''
            if (element.attributes.length > 0) {
                const attrs = Array.from(element.attributes)
                    .map(attr => `${attr.name}="${attr.value}"`)
                    .join(' ')
                attributesStr = ' ' + attrs
            }

            result += `<${tagName}${attributesStr}>`

            for (let i = 0; i < element.childNodes.length; i++) {
                if (!traverse(element.childNodes[i])) {
                    break
                }
            }

            result += `</${tagName}>`
        }

        return true
    }

    traverse(temp)

    // Add ellipsis if content was trimmed and showEllipsis is true
    if (trimmed && charCount >= maxChars && showEllipsis) {
        result += '...'
    }

    return result
}
